import { useContext, useEffect, useState } from 'react';
import { ExtendedOrderCache } from '../../../lib/cache/ExtendedOrderCache';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { CaseStatusType, getCaseStatusByStatusName } from '~/apps/self-serve/lib/helpers/ClaimAnalyser';
import { getApiPath } from '~/lib/api/self-serve/get-api-path';
import { authenticatedFetchWithTenantAnalytics, useAnalytics, useTenant, useTranslation } from '@parcel2go/npm-p2g-app';
import { useAuth } from 'react-oidc-context';
import { capturePromiseException } from '~/lib/metrics';
import { ApiResponse } from '~/lib/api/api-response';
import useSWR from 'swr';
import { CaseSearchApiCachedModel, GetCasesApiResponseModel } from '~/lib/api/self-serve/types';
import { ConcernsContext } from '~/apps/smartsend/lib/context/ConcernsContext';
import { TranslationNamespaces } from '~/lib/constants';

type summary = {
  [key in CaseStatusType]?: number;
};

const useClaimsBackgroundTask = () => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { session } = useAnalytics();
  const { tenant, settings } = useTenant();
  const { user } = useAuth();
  const [summary, setSummary] = useState<summary>({});
  const { dispatch } = useContext(ConcernsContext);

  const { data } = useSWR<CaseSearchApiCachedModel[]>(
    SettingsCache.settings.Features.includes('Claims') ? `${tenant}/claims/background` : null,
    async () => {
      const res: ApiResponse<GetCasesApiResponseModel> = await authenticatedFetchWithTenantAnalytics(
        user,
        tenant,
        settings,
        session,
        getApiPath(tenant, `/cases/CLAIMS/list`)
      )
        .then((res) => res.json())
        .catch(capturePromiseException);

      if (res.success && res.result) {
        return res.result.cases.map((x) => ({
          id: x.id,
          internalId: x.internalId,
          status: x.status,
          type: x.type
        }));
      }

      return null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: false,
      refreshWhenHidden: false,
      refreshInterval: (d) => (d === undefined ? 100 : 15 * 60000) // refresh every 15 minutes
    }
  );

  useEffect(() => {
    if (data?.length > 0) {
      // Create status summary
      const statusSummary: summary = {};
      data.forEach((c) => {
        const status = getCaseStatusByStatusName(c.status.name);
        statusSummary[status] = (statusSummary[status] || 0) + 1;
      });
      setSummary(statusSummary);

      // Assign to ExtendedOrderCache for various other places in the app to use
      data.forEach((x) => {
        if (!ExtendedOrderCache.claimOrderLineIds.find((c) => c.id === String(x.id))) {
          ExtendedOrderCache.claimOrderLineIds.push({
            id: String(x.id),
            orderlineId: x.internalId?.toString(),
            status: x.status.name
          });
        }
      });

      const requiresActions = statusSummary['Action'] || 0;
      if (requiresActions > 0) {
        dispatch({
          type: 'ADD-CONCERN',
          payload: {
            Title: t('NOTIFICATION_CLAIMS_TITLE', { defaultValue: 'Claims' }),
            Description: t('NOTIFICATION_CLAIMS_CONTENT', {
              defaultValue: `{{count}} claim(s) have outstanding actions.`,
              count: requiresActions
            }),
            Type: 'claim'
          }
        });
      }
    }
  }, [data, dispatch, t]);

  return summary;
};

export default useClaimsBackgroundTask;
